import { BellIcon, EyeIcon, PersonAddIcon, PlusIcon, TrashIcon } from '@primer/octicons-react';
import { Box, Label, Tooltip, useConfirm } from '@primer/react';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { clbNgoaiKhoaApi } from '../../api/clbNgoaiKhoaApi';
import Button from '../../components/ui/button/Button';
import DataTable from '../../components/ui/data-table';
import { IColumn } from '../../components/ui/data-table/DataTable';
import Text from '../../components/ui/text';
import { useCommonContext } from '../../contexts/common';
import { NotifyHelper } from '../../helpers/toast';
import { useLanguage } from '../../hooks/useLanguage';
import { IMeeting } from '../../models/response/meeting/IMeeting';
import { IMeetingVm } from '../../models/response/meeting/IMeetingVm';
import { meetingApi } from '../../api/meetingApi';

const MeetingPage = () => {

    const [isLoading, setIsLoading] = useState(false);
    const [dataSource, setDataSource] = useState<IMeeting[]>([]);
    const { translate } = useCommonContext();
    const confirm = useConfirm();
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        handleReloadAsync();
    }, [])
    const handleReloadAsync = async () => {
        setIsLoading(true);
        const res = await meetingApi.select();
        setIsLoading(false)
        if (res.is_success) {
            setDataSource(res.data)
        } else {
            NotifyHelper.Error(res.message ?? "Có lỗi")
        }
    }
    const handleSendNotify = async (id: number) => {
        if (await confirm({
            content: "Gửi thông báo tới PHHS",
            title: "Xác nhận",
            cancelButtonContent: "Đóng",
            confirmButtonContent: "Gửi thông báo",
            confirmButtonType: "primary"
        })) {
            setIsSaving(true);
            const res = await meetingApi.sendNotify(id);
            setIsSaving(false);
            if (res.is_success) {
                handleReloadAsync();
                NotifyHelper.Success("Success");
            } else {
                NotifyHelper.Error(res.message ?? "");
            }
        }
    }
    const onDelete = async (clbId: number) => {
        if (await confirm({
            content: "Bạn có chắc chắn muốn xóa nội dung này",
            title: "Xóa câu lạc bộ",
            cancelButtonContent: "Không xóa",
            confirmButtonContent: "Xóa",
            confirmButtonType: "danger"
        })) {
            const res = await meetingApi.delete(clbId);
            if (res.is_success) {
                handleReloadAsync();
                NotifyHelper.Success("Success");
            } else {
                NotifyHelper.Error(res.message ?? "Error")
            }
        }
    }
    const columns = useMemo((): any => {
        let result: IColumn[] = [
            {
                dataField: "name",
                minWidth: "180px",
                isMainColumn: true,
                caption: `${translate('Base.Label.Name')}`,
                cellRender: (data: IMeeting) => {
                    return (
                        <Box sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "baseline"
                        }}>
                            <Link to={`../../meeting/detail/${data.id}`}>
                                {data.name}
                            </Link>
                        </Box>
                    );
                }
            },
            {
                dataField: "is_published",
                width: "120px",
                caption: `${translate('Base.Label.Status')}`,
                cellRender: (data: IMeeting) => {
                    return (
                        <>
                            <Box sx={{ display: "flex", flexDirection: "row" }}>
                                {data.is_published &&
                                    <Tooltip text='Đã publish, PHHS có thể nhìn thấy'>
                                        <Button variant='invisible' text='Đã publish' leadingVisual={EyeIcon} />
                                    </Tooltip>
                                }
                                {!data.is_published &&
                                    <Tooltip text='Chưa publish, PHHS chưa thể nhìn thấy'>
                                        <Button variant='danger' text='Chưa publish' leadingVisual={EyeIcon} />
                                    </Tooltip>
                                }
                            </Box>
                        </>
                    );
                }
            },
            {
                dataField: "is_allow_re_submit",
                width: "180px",
                caption: `${translate('Đăng ký lại')}`,
                cellRender: (data: IMeeting) => {
                    return (
                        <>
                            <Box sx={{ display: "flex", flexDirection: "row" }}>
                                {data.is_allow_re_submit &&
                                    <Tooltip text='PHHS có thể đăng ký'>
                                        <Button variant='invisible' text='Cho phép đăng ký lại'/>
                                    </Tooltip>
                                }
                                {!data.is_allow_re_submit &&
                                    <Tooltip text='PHHS chưa thể đăng ký'>
                                        <Button variant='danger' text='Không cho đăng ký lại' />
                                    </Tooltip>
                                }
                            </Box>
                        </>
                    );
                }
            },
            {
                dataField: "app_notify_id",
                width: "200px",
                caption: `${translate('Thông báo')}`,
                cellRender: (data: IMeeting) => {
                    return (
                        <>
                            <Box sx={{ display: "flex", flexDirection: "row" }}>
                                {(data?.app_notify_id ?? 0) > 0 &&
                                    <Tooltip text='Phụ huynh đã nhận được thông báo đăng ký về Meeting'>
                                        <Button variant='invisible' text='Đã Gửi thông báo' leadingVisual={BellIcon} />
                                    </Tooltip>
                                }
                                {(data?.app_notify_id ?? 0) <= 0 &&
                                    <Tooltip text='Chưa gửi thông báo tới PHHS'>
                                        <Button variant='danger' text='Chưa gửi thông báo' leadingVisual={BellIcon} onClick={() => {
                                            if (data.is_published) {
                                                handleSendNotify(data.id)
                                            } else {
                                                NotifyHelper.Error("Vui lòng publish Meeting trước")
                                            }
                                        }} />
                                    </Tooltip>
                                }
                            </Box>
                        </>
                    );
                }
            },
            // {
            //     id: "da_dang_ky",
            //     width: "130px",
            //     caption: `${translate('Đã đăng ký')}`,
            //     cellRender: (data: IMeetingVm) => {
            //         return (
            //             <Box sx={{
            //                 display: "flex",
            //                 flexDirection: "column",
            //                 alignItems: "baseline"
            //             }}>
            //                 <Link to={`../../meeting/${data.id}/result`} style={{
            //                     textDecoration: "none"
            //                 }}>
            //                     <Button count={data.total_registed ?? 0} text='Base.Label.ViewResult'
            //                         variant='invisible'
            //                     />
            //                 </Link>

            //             </Box>
            //         );
            //     }
            // },
            {
                id: "cmd",
                width: "100px",
                caption: `${translate('Base.Label.Command')}`,
                cellRender: (data: IMeeting) => {
                    return (
                        <>
                            <Button icon={TrashIcon} aria-label='' variant='danger' onClick={() => {
                                onDelete(data.id)
                            }} />
                        </>
                    );
                }
            },
        ];

        return result;
    }, []);

    return (
        <Box sx={{
            // p: 3
        }}>
            <Box>
                <DataTable
                    titleComponent={
                        <>
                            <Text text={`${translate("Danh sách Meeting")}`} sx={{
                                fontSize: "24px"
                            }} />
                        </>}
                    subTitle={`${translate("Base.Label.TotalCount")}: ${(dataSource?.length ?? 0).toLocaleString()}`}
                    searchEnable
                    isLoading={isLoading}
                    actionComponent={
                        <Box sx={{ display: "flex" }}>
                            <Link to={`../../meeting/detail/0`} style={{
                                textDecoration: "none"
                            }}>
                                <Button text='Add new' leadingVisual={PlusIcon} variant='primary'
                                    size='medium'
                                />
                            </Link>
                            <Button text='Base.Label.Register.Behalf' leadingVisual={PersonAddIcon} variant='default' disabled size='medium'
                                sx={{ ml: 2 }}
                            />
                        </Box>
                    }
                    columns={columns}
                    data={dataSource}
                    height={`${window.innerHeight - 250}px`}
                />
            </Box>
        </Box>
    );
};

export default MeetingPage;